<script setup lang="ts">
import { inject, nextTick, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppButton from '~/components/common/AppButton.vue'
import Spinner from '~/components/common/Spinner.vue'
import type { ArcGisAddressAttributes, NuxtResponse } from '~/shared/api/dto'
import type { AdrsrvAddress, AdrsrvApiResponse } from '~/shared/types/adrsrv'
import AddressMap from './AddressMap.vue'
import AddressMapWrapper from './AddressMapWrapper.vue'

const router = useRouter()
const route = useRoute()

const addressStore = useAppAddressStore()
const objectStore = useObjectStore()
const envMetadata = inject<{ testing: boolean }>('envMetadata')

const addressSearchField = ref<string>('')
const addressSearchWatcherPaused = ref<boolean>(false)

const addressSuggestionList = ref<AdrsrvAddress[]>([])

const showHint = ref<boolean>(false)
const inlineError = ref<string | null>(null)
const isLoading = ref<boolean>(false)

const addressInput = ref<HTMLInputElement | null>(null)
const hasTouchedAddressInput = ref<boolean>(false)

const searchBarDiv = ref<HTMLElement | null>(null)
const smartMapSearch = ref<InstanceType<typeof AddressMap> | null>(null)

const MIN_CHARACTERS_FOR_SUGGESTIONS = 2
const getAddressSuggestions = async (): Promise<void> => {
	if (addressSearchField.value.length >= MIN_CHARACTERS_FOR_SUGGESTIONS) {
		const { data } = await $fetch<NuxtResponse<AdrsrvApiResponse>>('/api/adrsrv/adrcd', {
			params: { searchString: addressSearchField.value },
		})

		if (!data) {
			inlineError.value = 'Fehler beim Abrufen der Adressvorschläge.'
			return
		}

		const addresses = data.data || []

		if (addressSearchField.value.length >= MIN_CHARACTERS_FOR_SUGGESTIONS + 1 && !addresses) {
			inlineError.value = 'Keine passenden Adressvorschläge zum angegebenen Suchbegriff gefunden.'
		} else {
			inlineError.value = null
		}

		if (Array.isArray(addresses)) {
			addressSuggestionList.value = addresses
		}
	}
}

const clearAddressSearchFieldInput = () => {
	addressSearchField.value = ''
	addressStore.$reset()
	addressSuggestionList.value = []

	inlineError.value = null
}

const setAddressToStore = (address: AdrsrvAddress) => {
	addressStore.$patch({
		code: address.adrcd,
		latitude: address.lat,
		longitude: address.lon,
		text: address.text,
	})

	addressSearchWatcherPaused.value = true
	addressSearchField.value = address.text

	nextTick(() => (addressSearchWatcherPaused.value = false))

	addressSuggestionList.value = []

	// Call identify function from AddressSmartMapSearch
	if (smartMapSearch.value) {
		smartMapSearch.value.identify('', address.lon, address.lat, true)
	}
}

const updateAddressSearchField = (val: string) => {
	addressSearchWatcherPaused.value = true
	addressSearchField.value = val

	addressSuggestionList.value = []
	nextTick(() => (addressSearchWatcherPaused.value = false))
}

const locationQuery = async () => {
	isLoading.value = true
	inlineError.value = null

	try {
		navigator.geolocation.getCurrentPosition(
			async (position) => {
				trackPlausibleEvent('ActionPositionDetermined')
				if (position) {
					const { latitude: lat, longitude: long } = position.coords
					const { data } = await $fetch<NuxtResponse<ArcGisAddressAttributes[]>>('/api/arcgis/location', {
						params: {
							lat,
							long,
						},
					})

					if (data && data?.length > 0) {
						addressSuggestionList.value = data.map((item) => ({
							adrcd: item.ADRCD,
							text: `${item.STRASSE} ${item.HNR}, ${item.PLZ} ${item.GEMNAME}`,
							lat,
							lon: long,
						}))
					} else {
						inlineError.value = 'Leider konnte wir keine Adresse zu ihren Standort finden.'
					}
				} else {
					inlineError.value = 'Leider konnte wir keine Adresse zu ihren Standort finden.'
				}
			},
			(_error) => {
				inlineError.value = 'Leider konnte wir keine Adresse zu ihren Standort finden.'
			},
			{
				enableHighAccuracy: true,
			}
		)
	} finally {
		isLoading.value = false
	}
}

const focusInput = () => {
	if (addressInput.value) addressInput.value.focus()
}

const handleBlur = () => {
	hasTouchedAddressInput.value = true
}

const handleDisabledButtonClick = (_type: 'heizung' | 'pv') => {
	showHint.value = true
}

onMounted(focusInput)

watch(addressSearchField, async (): Promise<void> => {
	if (!addressSearchWatcherPaused.value) await getAddressSuggestions()
})

watch(
	() => addressStore.code,
	(newCode) => {
		objectStore.$reset()
		if (newCode) {
			router.replace({
				query: { ...route.query, adrcd: newCode },
			})
		} else {
			const query = { ...route.query }
			delete query.adrcd
			router.replace({ query })
		}
	}
)
</script>

<template>
	<div
		class="relative flex h-[calc(100dvh-141px)] w-full flex-col justify-between md:h-[calc(100dvh-169px)] lg:h-[calc(100dvh-185px)] xl:h-[calc(100dvh-201px)]"
	>
		<div ref="searchBarDiv" role="search" class="absolute z-30 w-full bg-white p-0 pb-8 md:grid md:grid-cols-2">
			<div class="mb-4 md:mb-0 md:border-r md:border-[--schwarz-15] md:pr-6">
				<div class="relative">
					<label class="relative flex flex-col gap-2">
						<strong class="text-lg md:text-xl">Adresse eingeben*</strong>
						<input
							ref="addressInput"
							v-model.trim="addressSearchField"
							autocomplete="street-address"
							class="w-full border border-[var(--schwarz-30)] bg-white bg-right bg-no-repeat p-2 pr-[75px] outline-none invalid:border-[--rot] invalid:shadow-custom-rot focus:border-[--blau] focus:shadow-custom-blau"
							type="text"
							name="address"
							placeholder="Adresse eingeben"
							minlength="2"
							aria-controls="object"
							aria-errormessage="error_search_input"
							:aria-invalid="!!((hasTouchedAddressInput && !addressStore.code) || inlineError)"
							required
							@blur="handleBlur"
						/>
						<img
							class="align-center absolute bottom-3 right-3 flex items-center"
							alt=""
							src="/assets/img/icons/magnifier.svg"
						/>
					</label>

					<button class="center align-center absolute right-9 top-9 flex p-2">
						<img
							src="/assets/img/icons/cross.svg"
							alt="Kreuz Icon"
							aria-label="Addressuche zurücksetzen"
							height="14px"
							width="14px"
							class="cursor-pointe mt-[6px]"
							@click="clearAddressSearchFieldInput"
						/>
					</button>

					<span id="error_search_input" data-testid="error_search_input">
						<div
							v-if="(hasTouchedAddressInput && !addressStore.code) || inlineError"
							class="absolute -z-10 mt-1 text-sm text-[--dunkelrot]"
						>
							{{
								inlineError
									? inlineError
									: showHint
										? 'Die eingegebene Adresse ist ungültig.'
										: 'Bitte gib eine gültige Adresse ein.'
							}}
						</div>
					</span>
				</div>

				<ul v-if="addressSuggestionList.length" class="z-10 divide-y divide-[var(--schwarz-30)] bg-white p-2">
					<li
						v-for="address in addressSuggestionList"
						:key="address.adrcd"
						class="cursor-pointer py-2 text-xs font-bold md:text-sm"
						tabindex="0"
						@click="setAddressToStore(address)"
						@keyup.enter="setAddressToStore(address)"
					>
						{{ address.text }}
					</li>
				</ul>

				<div v-if="!addressStore.code" class="mt-8 bg-white">
					<AppButton color="red" icon="md-gpsfixed" @click="locationQuery">
						Mein Standort<span v-if="isLoading">...</span>
					</AppButton>

					<Spinner v-if="isLoading" class="m-8" />
				</div>

				<div v-if="addressStore.text" id="object" class="mt-8" role="region" aria-live="polite">
					<p class="text-xl font-bold md:text-2xl">Objekt</p>
					<p class="text-s pt-2.5 md:text-lg">
						{{ addressStore.text.split(', ')[0] }}<br />
						{{ addressStore.text.split(', ')[1] }}
					</p>
				</div>
			</div>

			<div class="flex flex-col gap-2 md:pl-6">
				<p class="text-lg font-bold md:text-xl">EnergieKompass</p>

				<div v-if="addressStore.code" class="flex flex-col gap-2">
					<NuxtLink :to="{ name: 'object', query: { adrcd: addressStore.code } }" class="block">
						<AppButton color="red" tag="span" icon="house.svg"> Heizungs-Check starten </AppButton>
					</NuxtLink>

					<NuxtLink :to="{ name: 'pv-check', query: { adrcd: addressStore.code } }" class="block">
						<AppButton color="red" tag="span" icon="house.svg"> PV-Check starten </AppButton>
					</NuxtLink>

					<NuxtLink
						v-if="envMetadata?.testing"
						:to="{ name: 'sanierungs-check', query: { adrcd: addressStore.code } }"
						class="block"
					>
						<AppButton color="red" tag="span" icon="house.svg"> Sanierungs-Check starten </AppButton>
					</NuxtLink>
				</div>

				<div v-else class="flex flex-col gap-2">
					<AppButton
						color="gray"
						icon="house.svg"
						disabled
						aria-errormessage="error_search_input"
						@click="handleDisabledButtonClick('heizung')"
					>
						Heizungs-Check starten
					</AppButton>

					<AppButton
						color="gray"
						icon="house.svg"
						disabled
						aria-errormessage="error_search_input"
						@click="handleDisabledButtonClick('pv')"
					>
						PV-Check starten
					</AppButton>

					<AppButton
						v-if="envMetadata?.testing"
						color="gray"
						icon="house.svg"
						disabled
						aria-errormessage="error_search_input"
						@click="handleDisabledButtonClick('pv')"
					>
						Sanierungs-Check starten
					</AppButton>
				</div>

				<!-- Error message below both buttons -->
				<div
					v-if="
						(!addressStore.code && ((hasTouchedAddressInput && !addressStore.code) || inlineError)) ||
						showHint
					"
					class="mt-1 text-sm text-[--dunkelrot]"
				>
					{{
						inlineError
							? inlineError
							: showHint
								? 'Bitte gib zu erst eine gültige Adresse ein.'
								: 'Bitte gib zu erst eine gültige Adresse ein.'
					}}
				</div>
			</div>
		</div>

		<div class="relative h-full w-full overflow-hidden bg-[--background]">
			<AddressMapWrapper>
				<AddressMap ref="smartMapSearch" :update-address-search-field="updateAddressSearchField" />
			</AddressMapWrapper>
		</div>
	</div>
</template>
